import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Store, Loader2, Globe, Palette, Plus, Sun, Moon, Upload, CreditCard, CheckCircle, XCircle, ExternalLink, Eye, Unlink, MapPin, Tag, Check, Bell } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';

export default function Settings() {
  const { t, lang, setLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [connectingStripe, setConnectingStripe] = useState(false);
  const [disconnectingStripe, setDisconnectingStripe] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const [name, setName] = useState('');
  const [addressStreet, setAddressStreet] = useState('');
  const [addressCity, setAddressCity] = useState('');
  const [addressZip, setAddressZip] = useState('');
  const [phone, setPhone] = useState('');
  const [currency, setCurrency] = useState('eur');
  const [logoUrl, setLogoUrl] = useState('');
  const [logoTimestamp, setLogoTimestamp] = useState('');
  const [brandColor, setBrandColor] = useState('#F97316');
  const [receiptFooter, setReceiptFooter] = useState('');

  const [showNewStore, setShowNewStore] = useState(false);
  const [newStoreName, setNewStoreName] = useState('');
  const [creatingStore, setCreatingStore] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [storePromo, setStorePromo] = useState(null);
  const [notifPrefs, setNotifPrefs] = useState({ payment_received: true, payment_refunded: true, daily_summary: false });
  const [savingNotifs, setSavingNotifs] = useState(false);

  useEffect(() => {
    const fetchStore = async () => {
      try {
        const [storeRes, stripeRes, notifRes] = await Promise.all([
          api.get('/store/profile'),
          api.get('/stripe/status'),
          api.get('/store/notifications')
        ]);
        const data = storeRes.data;
        setStore(data);
        setName(data.name || '');
        setAddressStreet(data.address_street || '');
        setAddressCity(data.address_city || '');
        setAddressZip(data.address_zip || '');
        setPhone(data.phone || '');
        setCurrency(data.currency || 'eur');
        setLogoUrl(data.logo_url || '');
        setBrandColor(data.brand_color || '#F97316');
        setReceiptFooter(data.receipt_footer || '');
        setStripeStatus(stripeRes.data);
        setNotifPrefs(notifRes.data);

        // Check promo status
        if (data.promo_code && data.promo_expires_at) {
          const isActive = new Date(data.promo_expires_at) > new Date();
          setStorePromo({
            code: data.promo_code,
            expires_at: data.promo_expires_at,
            active: isActive,
            discount_percent: data.promo_discount_percent || 100
          });
        }

        // Check if returning from Stripe Connect onboarding
        const params = new URLSearchParams(window.location.search);
        if (params.get('stripe_connected') === 'true') {
          if (stripeRes.data.connected) {
            toast.success('Stripe account connected successfully!');
          } else {
            toast.info('Stripe onboarding in progress. Status will update shortly.');
          }
          window.history.replaceState({}, '', window.location.pathname);
        }
      } catch (err) {
        toast.error('Failed to load store profile');
      } finally {
        setLoading(false);
      }
    };
    fetchStore();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.put('/store/profile', {
        name,
        address_street: addressStreet,
        address_city: addressCity,
        address_zip: addressZip,
        phone,
        currency,
        logo_url: logoUrl,
        brand_color: brandColor,
        receipt_footer: receiptFooter
      });
      setStore(res.data);
      toast.success('Store profile updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnectStripe = async () => {
    setDisconnectingStripe(true);
    try {
      await api.post('/stripe/disconnect');
      setStripeStatus({ connected: false, stripe_account_id: null });
      setShowDisconnectConfirm(false);
      toast.success('Stripe account disconnected');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disconnect Stripe');
    } finally {
      setDisconnectingStripe(false);
    }
  };

  const getLogoSrc = (url) => {
    if (!url) return '';
    const base = url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return logoTimestamp ? `${base}?t=${logoTimestamp}` : base;
  };

  if (loading) {
    return <div className="h-64 bg-slate-100 animate-pulse" />;
  }

  return (
    <div className="max-w-lg space-y-6 animate-fade-in-up" data-testid="settings-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {t('settings.title')}
        </h1>
        <p className="text-sm text-slate-500 mt-1">{t('settings.subtitle')}</p>
      </div>

      {/* Language Selector */}
      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-slate-400" />
            <CardTitle className="text-base">{t('settings.language')}</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Button
              variant={lang === 'en' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setLanguage('en')}
              className={lang === 'en' ? 'bg-slate-900 text-white' : ''}
              data-testid="lang-en-btn"
            >
              English
            </Button>
            <Button
              variant={lang === 'fr' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setLanguage('fr')}
              className={lang === 'fr' ? 'bg-slate-900 text-white' : ''}
              data-testid="lang-fr-btn"
            >
              Français
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stripe Connect */}
      {user?.role === 'owner' && (
        <Card className="border-border" data-testid="stripe-connect-card">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-muted-foreground" />
              <CardTitle className="text-base">Stripe Connect</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {stripeStatus?.connected ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-emerald-600">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium text-sm">Stripe account connected</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Account ID: <span className="font-mono">{stripeStatus.stripe_account_id}</span>
                </p>
                <div className="flex gap-2 text-xs">
                  {stripeStatus.charges_enabled && <span className="bg-emerald-500/10 text-emerald-600 px-2 py-0.5 font-medium">Charges enabled</span>}
                  {stripeStatus.payouts_enabled && <span className="bg-emerald-500/10 text-emerald-600 px-2 py-0.5 font-medium">Payouts enabled</span>}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDisconnectConfirm(true)}
                  className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 mt-2"
                  data-testid="disconnect-stripe-btn"
                >
                  <Unlink className="w-4 h-4 mr-1.5" />
                  Disconnect Stripe
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-amber-600">
                  <XCircle className="w-5 h-5" />
                  <span className="font-medium text-sm">Stripe not connected</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Connect your Stripe account to receive payments directly. Funds go straight to your bank account.
                </p>
                <Button
                  onClick={async () => {
                    setConnectingStripe(true);
                    try {
                      const res = await api.post('/stripe/connect', { return_url: window.location.origin });
                      if (res.data.already_connected) {
                        toast.success('Stripe already connected!');
                        setStripeStatus({ ...stripeStatus, connected: true });
                      } else if (res.data.url) {
                        window.location.href = res.data.url;
                      }
                    } catch (err) {
                      const detail = err.response?.data?.detail || '';
                      if (detail.includes('platform profile')) {
                        toast.error('Stripe Connect is not yet available. The platform administrator must complete the Stripe Connect setup first.');
                      } else {
                        toast.error(detail || 'Failed to start Stripe onboarding. Please try again later.');
                      }
                    } finally {
                      setConnectingStripe(false);
                    }
                  }}
                  disabled={connectingStripe}
                  className="h-11 bg-[#635BFF] hover:bg-[#5851DB] text-white font-semibold"
                  data-testid="connect-stripe-btn"
                >
                  {connectingStripe ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                  {connectingStripe ? 'Connecting...' : 'Connect with Stripe'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <Store className="w-5 h-5 text-slate-400" />
            <CardTitle className="text-base">{t('settings.storeProfile')}</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="store-name">{t('settings.storeName')}</Label>
              <Input
                id="store-name"
                value={name}
                onChange={e => setName(e.target.value)}
                className="h-12 text-base"
                data-testid="store-name-input"
              />
            </div>

            {/* Structured Address Fields */}
            <div className="space-y-3">
              <div className="flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <Label>{t('settings.address')}</Label>
              </div>
              <Input
                value={addressStreet}
                onChange={e => setAddressStreet(e.target.value)}
                placeholder="123 Rue de la Paix"
                className="h-12 text-base"
                data-testid="store-address-street-input"
              />
              <div className="grid grid-cols-5 gap-3">
                <Input
                  value={addressZip}
                  onChange={e => setAddressZip(e.target.value)}
                  placeholder="75001"
                  className="h-12 text-base col-span-2"
                  data-testid="store-address-zip-input"
                />
                <Input
                  value={addressCity}
                  onChange={e => setAddressCity(e.target.value)}
                  placeholder="Paris"
                  className="h-12 text-base col-span-3"
                  data-testid="store-address-city-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="store-phone">{t('settings.phone')}</Label>
              <Input
                id="store-phone"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder="+33 1 23 45 67 89"
                className="h-12 text-base"
                data-testid="store-phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('settings.currency')}</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger className="h-12 text-base" data-testid="currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="eur">EUR - Euro</SelectItem>
                  <SelectItem value="usd">USD - US Dollar</SelectItem>
                  <SelectItem value="gbp">GBP - British Pound</SelectItem>
                  <SelectItem value="chf">CHF - Swiss Franc</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              type="submit"
              disabled={saving}
              className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase tracking-wide"
              data-testid="save-settings-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {saving ? t('settings.saving') : t('settings.saveChanges')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Receipt Branding */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Palette className="w-5 h-5 text-muted-foreground" />
              <CardTitle className="text-base">Receipt Branding</CardTitle>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open('/receipt/sample', '_blank')}
              data-testid="preview-receipt-btn"
            >
              <Eye className="w-4 h-4 mr-1.5" />
              Preview
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Logo</Label>
            <div className="flex items-center gap-3">
              <div className="w-20 h-20 bg-muted flex items-center justify-center border-2 border-dashed border-border overflow-hidden flex-shrink-0 relative" data-testid="logo-preview-box">
                {logoUrl ? (
                  <img
                    src={getLogoSrc(logoUrl)}
                    alt="Logo"
                    className="max-w-full max-h-full object-contain"
                    data-testid="logo-preview-img"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                    }}
                  />
                ) : null}
                <div
                  className="flex flex-col items-center justify-center text-muted-foreground"
                  style={{ display: logoUrl ? 'none' : 'flex' }}
                >
                  <Upload className="w-5 h-5 mb-0.5" />
                  <span className="text-[10px] font-medium">No logo</span>
                </div>
              </div>
              <div className="flex-1">
                <label className="flex items-center justify-center gap-2 h-11 px-4 border border-dashed border-border cursor-pointer hover:bg-muted transition-colors text-sm font-medium text-muted-foreground">
                  {uploadingLogo ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  {uploadingLogo ? 'Uploading...' : logoUrl ? 'Change Logo' : 'Upload Logo'}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                    className="hidden"
                    data-testid="logo-upload-input"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 2 * 1024 * 1024) {
                        toast.error('File too large (max 2MB)');
                        return;
                      }
                      setUploadingLogo(true);
                      try {
                        const formData = new FormData();
                        formData.append('file', file);
                        const res = await api.post('/store/logo', formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        setLogoUrl(res.data.logo_url);
                        setLogoTimestamp(Date.now().toString());
                        toast.success('Logo uploaded successfully');
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Upload failed');
                      } finally {
                        setUploadingLogo(false);
                        e.target.value = '';
                      }
                    }}
                  />
                </label>
                <p className="text-xs text-muted-foreground mt-1">PNG, JPEG, WebP, SVG. Max 2MB.</p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Brand Color</Label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                value={brandColor}
                onChange={e => setBrandColor(e.target.value)}
                className="w-11 h-11 cursor-pointer border border-border"
                data-testid="brand-color-input"
              />
              <Input
                value={brandColor}
                onChange={e => setBrandColor(e.target.value)}
                placeholder="#F97316"
                className="h-11 font-mono flex-1"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Receipt Footer Text</Label>
            <Input
              value={receiptFooter}
              onChange={e => setReceiptFooter(e.target.value)}
              placeholder="Thank you for your visit!"
              className="h-11"
              data-testid="receipt-footer-input"
            />
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full h-10 bg-foreground text-background hover:bg-foreground/90"
            data-testid="save-branding-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {saving ? 'Saving...' : 'Save Branding'}
          </Button>
        </CardContent>
      </Card>

      {/* Appearance */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            {theme === 'dark' ? <Moon className="w-5 h-5 text-muted-foreground" /> : <Sun className="w-5 h-5 text-muted-foreground" />}
            <CardTitle className="text-base">Appearance</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Button
              variant={theme === 'light' ? 'default' : 'outline'}
              size="sm"
              onClick={() => toggleTheme()}
              className={theme === 'light' ? 'bg-foreground text-background' : ''}
              data-testid="theme-light-btn"
            >
              <Sun className="w-4 h-4 mr-1.5" /> Light
            </Button>
            <Button
              variant={theme === 'dark' ? 'default' : 'outline'}
              size="sm"
              onClick={() => toggleTheme()}
              className={theme === 'dark' ? 'bg-foreground text-background' : ''}
              data-testid="theme-dark-btn"
            >
              <Moon className="w-4 h-4 mr-1.5" /> Dark
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Promo Code */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-muted-foreground" />
            <CardTitle className="text-base">Notifications</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">Choose which email notifications you want to receive.</p>
          {[
            { key: 'payment_received', label: 'Payment Received', desc: 'Get an email when a customer completes a payment' },
            { key: 'payment_refunded', label: 'Refund Processed', desc: 'Get an email when a refund is processed' },
            { key: 'daily_summary', label: 'Daily Summary', desc: 'Receive a daily summary of your transactions' },
          ].map(item => (
            <div key={item.key} className="flex items-center justify-between gap-4 py-2">
              <div className="flex-1">
                <p className="text-sm font-medium">{item.label}</p>
                <p className="text-xs text-muted-foreground">{item.desc}</p>
              </div>
              <Switch
                checked={notifPrefs[item.key]}
                onCheckedChange={async (checked) => {
                  const newPrefs = { ...notifPrefs, [item.key]: checked };
                  setNotifPrefs(newPrefs);
                  setSavingNotifs(true);
                  try {
                    await api.put('/store/notifications', newPrefs);
                    toast.success('Notification preferences updated');
                  } catch {
                    toast.error('Failed to update');
                    setNotifPrefs(prev => ({ ...prev, [item.key]: !checked }));
                  } finally { setSavingNotifs(false); }
                }}
                data-testid={`notif-${item.key}-switch`}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Promo Code */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <Tag className="w-5 h-5 text-muted-foreground" />
            <CardTitle className="text-base">Promo Code</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {storePromo?.active ? (
            <div className="space-y-2" data-testid="active-promo-info">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-emerald-500" />
                <span className="font-mono font-bold text-sm">{storePromo.code}</span>
                <span className="text-xs text-emerald-600 font-medium">Active</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {storePromo.discount_percent}% discount on platform fees — expires {new Date(storePromo.expires_at).toLocaleDateString('fr-FR')}
              </p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="promo-apply-section">
              <p className="text-xs text-muted-foreground">Have a promo code? Apply it to get a discount on platform fees.</p>
              <div className="flex gap-2">
                <Input
                  value={promoInput}
                  onChange={e => setPromoInput(e.target.value.toUpperCase())}
                  placeholder="Enter promo code"
                  className="h-11 font-mono uppercase flex-1"
                  data-testid="settings-promo-input"
                />
                <Button
                  onClick={async () => {
                    if (!promoInput.trim()) return;
                    setApplyingPromo(true);
                    try {
                      const res = await api.post('/promo/apply', { code: promoInput });
                      setStorePromo({
                        code: res.data.code,
                        expires_at: res.data.expires_at,
                        active: true,
                        discount_percent: res.data.discount_percent
                      });
                      setPromoInput('');
                      toast.success(res.data.message);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Invalid promo code');
                    } finally {
                      setApplyingPromo(false);
                    }
                  }}
                  disabled={applyingPromo || !promoInput.trim()}
                  className="h-11 bg-primary text-primary-foreground font-bold px-6"
                  data-testid="apply-promo-btn"
                >
                  {applyingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Multi-Store Management */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Store className="w-5 h-5 text-muted-foreground" />
              <CardTitle className="text-base">Stores</CardTitle>
            </div>
            <Button size="sm" variant="outline" onClick={() => setShowNewStore(true)} data-testid="new-store-settings-btn">
              <Plus className="w-4 h-4 mr-1" /> New Store
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground mb-3">
            Switch between stores from the sidebar. Active store: <span className="font-semibold text-foreground">{store?.name}</span>
          </p>
          <p className="text-xs text-muted-foreground">
            Store ID: <span className="font-mono">{store?.store_id}</span>
          </p>
          <p className="text-xs text-muted-foreground">
            Created: {store?.created_at ? new Date(store.created_at).toLocaleDateString('fr-FR') : 'N/A'}
          </p>
        </CardContent>
      </Card>

      {/* New Store Dialog */}
      <Dialog open={showNewStore} onOpenChange={setShowNewStore}>
        <DialogContent data-testid="new-store-dialog">
          <DialogHeader>
            <DialogTitle>Create New Store</DialogTitle>
            <DialogDescription>Add a new store location to your account.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label>Store Name</Label>
              <Input
                value={newStoreName}
                onChange={e => setNewStoreName(e.target.value)}
                placeholder="My New Store"
                className="h-11"
                data-testid="new-store-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewStore(false)}>Cancel</Button>
            <Button
              disabled={creatingStore || !newStoreName}
              className="bg-primary text-primary-foreground"
              onClick={async () => {
                setCreatingStore(true);
                try {
                  await api.post('/stores/create', { name: newStoreName });
                  toast.success(`Store "${newStoreName}" created!`);
                  setShowNewStore(false);
                  setNewStoreName('');
                  window.location.reload();
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to create store');
                } finally {
                  setCreatingStore(false);
                }
              }}
              data-testid="create-store-submit-btn"
            >
              {creatingStore ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {creatingStore ? 'Creating...' : 'Create Store'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stripe Disconnect Confirmation Dialog */}
      <Dialog open={showDisconnectConfirm} onOpenChange={setShowDisconnectConfirm}>
        <DialogContent data-testid="disconnect-stripe-dialog">
          <DialogHeader>
            <DialogTitle>Disconnect Stripe Account</DialogTitle>
            <DialogDescription>
              Are you sure you want to disconnect your Stripe account? You will no longer be able to accept payments until you reconnect.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDisconnectConfirm(false)} data-testid="cancel-disconnect-btn">
              Cancel
            </Button>
            <Button
              onClick={handleDisconnectStripe}
              disabled={disconnectingStripe}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-disconnect-btn"
            >
              {disconnectingStripe ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Unlink className="w-4 h-4 mr-2" />}
              {disconnectingStripe ? 'Disconnecting...' : 'Disconnect'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
